import type { ReactElement } from 'react';
import type { WidgetProps } from '@captomi/schema-form';

// Editable weekly-hours widget for `availability.green_zones` (schema widget key 'green-zones').
// Replaces the read-only JSON the console showed before, so a painter can set the days/times they
// take estimates. Emits a valid GreenZone[] — day ∈ monday..sunday, start/end "HH:MM" (24h, from
// <input type="time">), repeating:true — matching @captomi/captomi-office-schema greenZoneSchema.

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
interface Zone {
  day: string;
  start: string;
  end: string;
  repeating?: boolean;
}
const cap = (s: string): string => (s ? s[0]!.toUpperCase() + s.slice(1) : s);
// HH:MM is zero-padded 24h, so a string compare is chronological. A window is invalid when both
// ends are set and end is not after start — mirrors greenZoneSchema's refine, surfaced inline so
// the painter sees it before saving (the schema is the actual gate on save).
const isInvalid = (z: Zone): boolean => Boolean(z.start) && Boolean(z.end) && z.end <= z.start;

export function GreenZonesWidget({ value, onChange }: WidgetProps): ReactElement {
  const zones: Zone[] = Array.isArray(value) ? (value as Zone[]) : [];
  const update = (i: number, patch: Partial<Zone>): void =>
    onChange(zones.map((z, idx) => (idx === i ? { ...z, ...patch } : z)));
  const remove = (i: number): void => onChange(zones.filter((_, idx) => idx !== i));
  const add = (): void => onChange([...zones, { day: 'monday', start: '09:00', end: '17:00', repeating: true }]);

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {zones.length === 0 && <p className="muted" style={{ margin: 0 }}>No weekly hours set yet.</p>}
      {zones.map((z, i) => {
        const bad = isInvalid(z);
        return (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={z.day} onChange={(e) => update(i, { day: e.target.value })} style={{ padding: 4 }}>
              {DAYS.map((d) => (
                <option key={d} value={d}>{cap(d)}</option>
              ))}
            </select>
            <input type="time" value={z.start ?? ''} onChange={(e) => update(i, { start: e.target.value })} style={{ padding: 4 }} />
            <span aria-hidden>–</span>
            <input
              type="time"
              value={z.end ?? ''}
              onChange={(e) => update(i, { end: e.target.value })}
              aria-invalid={bad}
              style={{ padding: 4, ...(bad ? { borderColor: 'var(--danger)' } : {}) }}
            />
            <button type="button" onClick={() => remove(i)} aria-label="Remove" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16 }}>
              ✕
            </button>
            {bad && (
              <span className="error" role="alert" style={{ flexBasis: '100%', fontSize: 12, margin: 0 }}>
                End time must be after start time.
              </span>
            )}
          </div>
        );
      })}
      <div>
        <button type="button" className="btn" onClick={add} style={{ marginTop: 4 }}>
          Add hours
        </button>
      </div>
    </div>
  );
}
